import _ from 'lodash';
import { useSettingsStore } from '~/stores/settings';
import { getPresetCategorySets } from '~/util/presetCategories';
const level_sep = '>';
export const CLASSIFY_KEYS = ['app', 'title'];
const UNCATEGORIZED = ['Uncategorized'];
/** Canonical event fields offered in the category-rule editor. */
export const CANONICAL_SELECT_KEYS = CLASSIFY_KEYS;
/** ID of the implicit set holding a user's own (non-preset) categories. */
export const DEFAULT_SET_ID = 'default';
/** Drop empty/invalid select_keys so the rust parser never sees `[]`. */
export function normalizeSelectKeys(keys) {
    if (!keys || keys.length === 0) {
        return undefined;
    }
    const unique = [];
    for (const key of keys) {
        if (typeof key === 'string' && key && !unique.includes(key)) {
            unique.push(key);
        }
    }
    return unique.length > 0 ? unique : undefined;
}
/**
 * Merge multiple category sets in priority order (first set = highest priority).
 * When the same category name appears in multiple sets, the first occurrence wins.
 * Within each set, the standard specificity rule applies (deeper category wins).
 */
export function mergeCategorySets(sets) {
    const seen = new Set();
    const merged = [];
    for (const set of sets) {
        for (const cat of set.categories) {
            const key = JSON.stringify(cat.name);
            if (!seen.has(key)) {
                seen.add(key);
                merged.push(cat);
            }
        }
    }
    return merged;
}
const COLOR_UNCAT = '#CCC';
// The default categories
// Should be run through createMissingParents before being used in most cases.
export const defaultCategories = [
    {
        name: ['Work'],
        rule: { type: 'regex', regex: 'Google Docs|libreoffice|ReText' },
        data: { color: '#0F0', score: 10 },
    },
    {
        name: ['Work', 'Programming'],
        rule: {
            type: 'regex',
            regex: 'GitHub|Stack Overflow|BitBucket|Gitlab|vim|Spyder|kate|Ghidra|Scite',
        },
    },
    {
        name: ['Work', 'Programming', 'ActivityWatch'],
        rule: { type: 'regex', regex: 'ActivityWatch|aw-', ignore_case: true },
    },
    { name: ['Work', 'Image'], rule: { type: 'regex', regex: 'GIMP|Inkscape' } },
    { name: ['Work', 'Video'], rule: { type: 'regex', regex: 'Kdenlive' } },
    { name: ['Work', 'Audio'], rule: { type: 'regex', regex: 'Audacity' } },
    { name: ['Work', '3D'], rule: { type: 'regex', regex: 'Blender' } },
    {
        name: ['Media'],
        rule: { type: 'none' },
        data: { color: '#F33' },
    },
    {
        name: ['Media', 'Games'],
        rule: { type: 'regex', regex: 'Minecraft|RimWorld' },
        data: { color: '#F80' },
    },
    {
        name: ['Media', 'Video'],
        rule: { type: 'regex', regex: 'YouTube|Plex|VLC' },
        data: { color: '#F33' },
    },
    {
        name: ['Media', 'Social Media'],
        rule: {
            type: 'regex',
            regex: 'reddit|Facebook|Twitter|Instagram|devRant',
            ignore_case: true,
        },
        data: { color: '#FCC400' },
    },
    {
        name: ['Media', 'Music'],
        rule: {
            type: 'regex',
            regex: 'Spotify|Deezer',
            ignore_case: true,
        },
        data: { color: '#A8FC00' },
    },
    {
        name: ['Comms'],
        rule: { type: 'none' },
        data: { color: '#9FF' },
    },
    {
        name: ['Comms', 'IM'],
        rule: {
            type: 'regex',
            regex: 'Messenger|Telegram|Signal|WhatsApp|Rambox|Slack|Riot|Element|Discord|Nheko|NeoChat|Mattermost',
        },
    },
    { name: ['Comms', 'Email'], rule: { type: 'regex', regex: 'Gmail|Thunderbird|mutt|alpine' } },
    { name: ['Uncategorized'], rule: { type: null }, data: { color: COLOR_UNCAT } },
];
/**
 * The categories a set starts out with — what "restore defaults" restores.
 *
 * Normally the built-in `defaultCategories`, but builds/deployments that ship
 * preset category sets (see `~/util/presetCategories`) start from those instead.
 *
 * `setId` is the set the categories are destined for, so that restoring never
 * writes one set's categories into another:
 *  - a preset set restores its own categories,
 *  - a user-owned set restores the built-in defaults,
 *  - omitting it (install default) uses the first preset, which is the one
 *    `loadCategories()` activates on a fresh install.
 */
export function getDefaultClasses(setId) {
    const presets = getPresetCategorySets();
    if (presets.length === 0) {
        return _.cloneDeep(defaultCategories);
    }
    if (setId === undefined) {
        return presets[0].categories;
    }
    const preset = presets.find(p => p.id === setId);
    return preset ? preset.categories : _.cloneDeep(defaultCategories);
}
export function annotate(c) {
    const ch = c.name;
    c.name_pretty = ch.join(level_sep);
    c.subname = ch.slice(-1)[0];
    c.parent = ch.length > 1 ? ch.slice(0, -1) : null;
    c.depth = ch.length - 1;
    return c;
}
export function createMissingParents(classes) {
    // Creates parents for categories that are missing theirs (implicit parents)
    classes = _.cloneDeep(classes);
    classes = classes.slice().map(c => annotate(c));
    const all_full_names = new Set(classes.map(c => c.name.join(level_sep)));
    function _createMissing(children) {
        children
            .map(c => c.parent)
            .filter(p => !!p)
            .map(p => {
            const name = p.join(level_sep);
            if (p && !all_full_names.has(name)) {
                const new_parent = annotate({ name: p, rule: { type: null } });
                //console.log('Creating missing parent:', new_parent);
                classes.push(new_parent);
                all_full_names.add(name);
                // New parent might not be top-level, so we need to recurse
                _createMissing([new_parent]);
            }
        });
    }
    _createMissing(classes);
    return classes;
}
export function build_category_hierarchy(classes) {
    classes = createMissingParents(classes);
    function assignChildren(classes_at_level) {
        return classes_at_level.map(cls => {
            cls.children = classes.filter(child => {
                return child.parent && cls.name
                    ? JSON.stringify(child.parent) == JSON.stringify(cls.name)
                    : false;
            });
            assignChildren(cls.children);
            return cls;
        });
    }
    return assignChildren(classes.filter(c => !c.parent));
}
export function flatten_category_hierarchy(hier) {
    return _.flattenDeep(hier.map(h => {
        const level = [h, flatten_category_hierarchy(h.children)];
        h.children = [];
        return level;
    }));
}
function areWeTesting() {
    return process.env.NODE_ENV === 'test';
}
export function saveClasses(classes) {
    if (areWeTesting()) {
        // TODO: move this into settings store?
        console.log('Not saving classes in test mode');
        return;
    }
    const settingsStore = useSettingsStore();
    settingsStore.update({ classes: classes.map(cleanCategory) });
    console.log('Saved classes', settingsStore.classes);
}
export function cleanCategory(cat) {
    cat = _.cloneDeep(cat);
    delete cat.children;
    delete cat.parent;
    delete cat.subname;
    delete cat.name_pretty;
    delete cat.depth;
    // in an older version, type could be null (which is not allowed)
    // we also want to strip any excess properties that may have belonged to another rule type
    if (cat.rule && (cat.rule.type === null || cat.rule.type === 'none')) {
        cat.rule = { type: 'none' };
    }
    else if (cat.rule && cat.rule.type === 'regex') {
        const keys = normalizeSelectKeys(cat.rule.select_keys);
        if (keys) {
            cat.rule.select_keys = keys;
        }
        else {
            delete cat.rule.select_keys;
        }
    }
    return cat;
}
export function loadClasses() {
    const settingsStore = useSettingsStore();
    return settingsStore.classes;
}
/**
 * Persist category sets and active set IDs to the settings store.
 * Also updates the legacy `classes` field for backwards compatibility with external readers.
 */
export function saveCategories(sets, activeIds) {
    if (areWeTesting()) {
        console.log('Not saving categories in test mode');
        return;
    }
    const settingsStore = useSettingsStore();
    const cleanSets = sets.map(s => ({ ...s, categories: s.categories.map(cleanCategory) }));
    const effectiveClasses = mergeCategorySets(sets.filter(s => activeIds.includes(s.id))).map(cleanCategory);
    return settingsStore.update({
        category_sets: cleanSets,
        active_set_ids: activeIds,
        classes: effectiveClasses,
    });
}
/**
 * True when `classes` is still an install default, not a user taxonomy.
 *
 * Used to tell "the user never configured categories" apart from a real edit.
 * Names and rules must match an install default. A missing `data.color` is
 * still unconfigured (so a later palette on the shipped preset does not look
 * like user data); a *different* color is a customization and is kept.
 */
function categoryNameKey(c) {
    return c.name.join('>');
}
function ruleSignature(c) {
    return JSON.stringify([
        // `null` and `'none'` are the same rule type after cleanCategory
        c.rule?.type === 'regex' ? 'regex' : 'none',
        c.rule?.type === 'regex' ? c.rule?.regex ?? null : null,
        Boolean(c.rule?.ignore_case),
        c.rule?.select_keys ?? null,
        c.rule?.priority ?? c.rule?.weight ?? null,
    ]);
}
function categoryColor(c) {
    const color = c.data && c.data.color;
    return typeof color === 'string' && color.length > 0 ? color : null;
}
/**
 * True when `stored` is the same taxonomy as `reference`, allowing a missing
 * color (install default, or a later palette on the preset) but not a color
 * the user actually changed.
 *
 * Score: only an explicitly set stored score that differs from the reference
 * is treated as a user customization.  A missing/undefined stored score is
 * indistinguishable from legacy data (categories persisted before scores were
 * introduced) and is therefore treated as an install default, not a user edit.
 * This means "Inherit parent score" (stores undefined) does not prevent preset
 * activation — an acceptable trade-off given the ambiguity.
 *
 * Duplicate stored names are treated as user edits (one-to-one name matching
 * is required, mirroring the uniqueness check on the reference side).
 */
function matchesInstallDefault(stored, reference) {
    if (stored.length !== reference.length)
        return false;
    const refByName = new Map(reference.map(c => [categoryNameKey(c), c]));
    if (refByName.size !== reference.length)
        return false;
    // Require one-to-one name matching: duplicates in stored would let a renamed/
    // deleted category slip through as "matching" by piggy-backing on a sibling.
    const storedNames = stored.map(c => categoryNameKey(c));
    if (new Set(storedNames).size !== stored.length)
        return false;
    for (const cat of stored) {
        const ref = refByName.get(categoryNameKey(cat));
        if (!ref)
            return false;
        if (ruleSignature(cat) !== ruleSignature(ref))
            return false;
        const storedColor = categoryColor(cat);
        if (storedColor !== null && storedColor !== categoryColor(ref))
            return false;
        // Only treat score as a user edit if it is explicitly set to a different
        // value.  A missing/undefined stored score is indistinguishable from legacy
        // data (persisted before scores existed), so we do not block on it.
        const storedScore = cat.data?.score;
        if (storedScore !== undefined && storedScore !== ref.data?.score)
            return false;
    }
    return true;
}
export function classesLookUnconfigured(classes) {
    // Empty array is a deliberate "no categories" save, not an install default.
    if (classes == null)
        return true;
    if (classes.length === 0)
        return false;
    if (matchesInstallDefault(classes, defaultCategories))
        return true;
    return getPresetCategorySets().some(p => matchesInstallDefault(classes, p.categories));
}
/**
 * Load category sets and active set IDs from the settings store.
 * Falls back to the legacy flat `classes` setting if no sets are defined yet.
 *
 * Preset sets shipped by the build/deployment (see `~/util/presetCategories`)
 * are always appended as *available* sets, but are only active by default when
 * the user has no stored categorization of their own. Persisted *install
 * defaults* (the stock `classes` list, or a copy of a shipped preset) do not
 * count — `settings.save()` writes every key, so a theme/view save on first
 * run used to look like a user taxonomy and let `default` silently win over
 * the preset (ActivityWatch/activitywatch#1439). A stored set with the same
 * id always wins over the preset definition, so user edits stick.
 */
export function loadCategories() {
    const settingsStore = useSettingsStore();
    const storedSets = settingsStore.category_sets;
    const storedActiveIds = settingsStore.active_set_ids;
    const presets = getPresetCategorySets();
    let sets;
    let activeIds;
    const storedOwnSets = Boolean(storedSets && storedSets.length > 0);
    // `hasStoredCategories` is true as soon as `classes` exists in storage,
    // which first-run `settings.save()` always writes. Only a *custom* class
    // list should suppress the shipped preset.
    const storedCustomClasses = settingsStore.hasStoredCategories &&
        !storedOwnSets &&
        !classesLookUnconfigured(settingsStore.classes);
    if (storedOwnSets) {
        sets = [...storedSets];
        activeIds =
            storedActiveIds && storedActiveIds.length > 0 ? [...storedActiveIds] : [storedSets[0].id];
    }
    else if (presets.length > 0 && !storedCustomClasses) {
        // First run on a build that ships presets: activate the first preset only.
        //
        // We deliberately limit the initial selection to one set: syncToPrimarySet()
        // in the category store cannot split state.classes back into individual sets
        // when multiple sets are active, so it skips the sync entirely. Activating
        // more than one preset on first run would therefore cause any category edit
        // the user makes to be lost on the next reload.
        //
        // The remaining presets are still appended below and are available in the UI
        // for the user to activate manually.
        sets = [];
        activeIds = [presets[0].id];
    }
    else {
        // Migration path: no sets defined yet — wrap the existing flat classes into a "default" set
        const legacyClasses = settingsStore.classes || defaultCategories;
        sets = [{ id: DEFAULT_SET_ID, categories: legacyClasses }];
        activeIds = [DEFAULT_SET_ID];
    }
    // Presets are always offered as sets the user can switch to/combine,
    // unless a set with the same id is already stored (that one is authoritative).
    for (const preset of presets) {
        if (!sets.some(s => s.id === preset.id)) {
            sets.push(preset);
        }
    }
    // Never return a dangling or empty selection
    if (sets.length === 0) {
        sets = [{ id: DEFAULT_SET_ID, categories: defaultCategories }];
    }
    activeIds = activeIds.filter(id => sets.some(s => s.id === id));
    if (activeIds.length === 0) {
        activeIds = [sets[0].id];
    }
    return { sets, activeIds };
}
function categoryRank(category) {
    const explicit = category.rule.priority ?? category.rule.weight;
    if (typeof explicit === 'number' && Number.isInteger(explicit)) {
        return explicit;
    }
    return category.name.length * 10;
}
function pickHighestRanked(categories) {
    return _.maxBy(categories, categoryRank);
}
export function matchString(str, categories, event) {
    if (!categories) {
        console.log('Categories not passed, loading... (if you see this outside of a test, you should probably pass them)');
        categories = loadClasses();
    }
    // Compile regexes
    const regexes = categories
        .filter(c => c.rule.type == 'regex')
        .map(c => {
        // using 'm' flag to make `$` and `^` in rules work
        const re = RegExp(c.rule.regex, (c.rule.ignore_case ? 'i' : '') + 'm');
        return [c, re];
    });
    // Find the matching category.
    // If several categories match, explicit priority wins; otherwise depth wins.
    const matchingCats = regexes.filter(([category, re]) => {
        const selectKeys = normalizeSelectKeys(category.rule.select_keys);
        if (event && selectKeys) {
            return selectKeys.some(key => {
                const value = event.data[key];
                return typeof value === 'string' && re.test(value);
            });
        }
        return re.test(str);
    });
    if (matchingCats.length > 0) {
        return pickHighestRanked(matchingCats.map(c => c[0]));
    }
    return null;
}
// this is used only in tests
export function classifyEvents(events, categories) {
    const regexes = categories
        .filter(c => c.rule.type == 'regex')
        .map(c => {
        const re = RegExp(c.rule.regex, c.rule.ignore_case ? 'i' : '');
        return [c, re];
    });
    return events.map((e) => {
        const matchingCats = regexes.filter(([category, re]) => {
            const keys = normalizeSelectKeys(category.rule.select_keys) || CLASSIFY_KEYS;
            return keys.some(key => {
                const value = e.data[key];
                return typeof value === 'string' && re.test(value);
            });
        });
        e.data.$category =
            matchingCats.length > 0
                ? pickHighestRanked(matchingCats.map(([category]) => category)).name
                : UNCATEGORIZED;
        return e;
    });
}
